import { useEffect } from 'react';
import { useMapEvents } from 'react-leaflet';
import { useUiStore } from '@/store/uiStore';
import { featureFlags } from '@/config/featureFlags';
import type { GeoLevel } from '@/types/geography';

/** Below this zoom, show counties (statewide overview). At/above it, ZIPs
 * are dense enough on screen to be readable rather than a solid smear of
 * thin borders. 8 is roughly "zoomed into a single metro region" on this
 * map's default Texas-wide starting zoom of 6. */
const ZIP_ZOOM_THRESHOLD = 8;

/** Below this zoom (but still >= ZIP_ZOOM_THRESHOLD), show ZIPs. At/above
 * it -- and only inside one of METRO_BOUNDING_BOXES below -- show named
 * neighborhoods instead. 11 is roughly "zoomed into a single part of a
 * city" (a few miles across). */
const NEIGHBORHOOD_ZOOM_THRESHOLD = 11;

/**
 * Generous (not tight) lat/lon bounding boxes for the four metro cores that
 * have a real neighborhood layer (see NeighborhoodLayer.tsx /
 * scripts/fetch-neighborhoods-osm.mjs). Zooming in anywhere else on the map
 * -- rural areas, other Texas cities -- correctly stops at ZIP, since no
 * neighborhood data exists there and this app never fabricates a layer it
 * can't back with real data.
 */
const METRO_BOUNDING_BOXES: Array<{ name: string; latMin: number; latMax: number; lonMin: number; lonMax: number }> = [
  { name: 'Houston', latMin: 29.4, latMax: 30.3, lonMin: -95.9, lonMax: -94.9 },
  { name: 'Dallas-Fort Worth', latMin: 32.4, latMax: 33.3, lonMin: -97.6, lonMax: -96.4 },
  { name: 'Austin', latMin: 29.9, latMax: 30.7, lonMin: -98.1, lonMax: -97.4 },
  { name: 'San Antonio', latMin: 29.1, latMax: 29.8, lonMin: -98.9, lonMax: -98.1 },
];

function isInsideAnyMetro(lat: number, lon: number): boolean {
  return METRO_BOUNDING_BOXES.some((b) => lat >= b.latMin && lat <= b.latMax && lon >= b.lonMin && lon <= b.lonMax);
}

function computeAutoGeoLevel(zoom: number, lat: number, lon: number): GeoLevel {
  if (featureFlags.ENABLE_ZILLOW && zoom >= NEIGHBORHOOD_ZOOM_THRESHOLD && isInsideAnyMetro(lat, lon)) {
    return 'neighborhood';
  }
  if (featureFlags.ENABLE_ZIP_LAYER && zoom >= ZIP_ZOOM_THRESHOLD) {
    return 'zip';
  }
  return 'county';
}

/**
 * Invisible helper, rendered as a child of <MapContainer> next to
 * MapFlyToController. Implements the literal behavior asked for: zooming in
 * anywhere on the map reveals ZIP-level boundaries, and zooming in further
 * inside Houston/Dallas-Fort Worth/Austin/San Antonio reveals named
 * neighborhoods -- instead of requiring the user to manually flip the
 * Layer dropdown. Only takes effect while autoGeoLevel is on (the
 * default) -- picking a layer manually from MapControls turns this off so
 * that choice sticks until the user opts back into "Auto".
 *
 * City stays manual-only (it's an approximate point-marker layer, not a
 * real boundary tier, so it isn't part of the zoom progression).
 */
export function MapZoomGeoLevelController() {
  const autoGeoLevel = useUiStore((s) => s.autoGeoLevel);
  const geoLevel = useUiStore((s) => s.geoLevel);
  const setGeoLevelFromZoom = useUiStore((s) => s.setGeoLevelFromZoom);

  const applyAutoLevel = (zoom: number, lat: number, lon: number) => {
    if (!autoGeoLevel) return;
    if (geoLevel === 'city') return; // manual-only tier, not zoom-driven
    const nextLevel = computeAutoGeoLevel(zoom, lat, lon);
    if (nextLevel !== geoLevel) setGeoLevelFromZoom(nextLevel);
  };

  const map = useMapEvents({
    zoomend: () => {
      const center = map.getCenter();
      applyAutoLevel(map.getZoom(), center.lat, center.lng);
    },
    moveend: () => {
      // Panning across a metro boundary at neighborhood zoom should also
      // re-evaluate (e.g. panning from inside Houston's bbox out into open
      // county between Houston and Austin) -- zoomend alone would miss this.
      const center = map.getCenter();
      applyAutoLevel(map.getZoom(), center.lat, center.lng);
    },
  });

  // Also apply once on mount (e.g. a saved/initial zoom that's already past
  // a threshold) rather than waiting for the first zoomend/moveend event.
  useEffect(() => {
    const center = map.getCenter();
    applyAutoLevel(map.getZoom(), center.lat, center.lng);
    // Intentionally only re-checking on mount / autoGeoLevel toggling --
    // zoom/pan-driven changes after that come from the handlers above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGeoLevel]);

  return null;
}
